import { VulnerabilitiesApiGetVulnerabilityByIdRequest } from '@ariksa/inventory-core';
import {
  ContextApiGetRiskContextResRequest,
  DashboardApiGetResourcesByImagesForDashboardRequest,
  InsightV2ApiInsightV2Request,
  InsightV2Pagination,
  ResourceApiGetResourceByUuidRequest,
  Resources,
  ResourceVulnerabilities,
  RiskContextResponse,
  SunburstDashboard,
  VulnerabilitiesApiGetCveResourcesRequest,
  VulnerabilitiesApiGetPackageResourcesRequest,
  VulnerabilitiesApiGetVulnerabilitiesRequest,
  VulnerabilitiesApiVulnerabilityInsightRequest,
} from '@ariksa/inventory-core/api';
import {
  AlertsApiGetEntitySeverityListRequest,
  EntitySeverityListResponse,
} from '@ariksa/notification';
import { PayloadAction } from '@reduxjs/toolkit';
import { forEach, values } from 'lodash';
import { QueryAction, QueryStatus } from 'services/types';
import { PagedQueryState } from 'services/utils/PagedQueryState';
import { QueryState } from 'services/utils/QueryState';
import {
  DefaultApiGetBySeverityDashboardDataRequest,
  DefaultApiGetBySourcesDashboardDataRequest,
  DefaultApiGetCsvReportRequest,
  DefaultApiGetCvesByIdRequest,
  DefaultApiGetCvesRequest,
  DefaultApiGetEcrImageListRequest,
  DefaultApiGetVulnerabilityPriortizationDataRequest,
  DefaultApiGetVulnerabilitySeverityCountTrendRequest,
  DefaultApiGetVulnerablePackagesRequest,
  DefaultApiGetVulnerableResourcesCountTrendRequest,
  ItemApiGetContainersRequest,
  ItemApiGetInstanceSummaryByIdRequest,
  ItemApiGetPatchRecommendationsForResourceRequest,
  ItemApiGetVulnerabilityByIdRequest,
} from 'services/Vulnerability/legacyRequestTypes';
import {
  ContainerInfo,
  InstanceSummary,
  VulnerabilityBySeverityDashboard,
  VulnerabilityCvePage,
  VulnerabilityDashboardBySources,
  VulnerabilityImageDashboard,
  VulnerabilityPage,
  VulnerabilityPatchRecommendationResource,
  VulnerabilityPrioritization,
  VulnerablePackagePage,
} from 'services/Vulnerability/types';

import { mapDashboardToLegacy } from 'api/scan-vault/legacy';
import {
  PrioritizationFlow,
  PrioritizationFlowParams,
} from 'api/scan-vault/prioritizationFlow';
import { Sorted } from 'components/DataDisplay/NewTable/types';
import { PageInfo, publishedAtOptions } from 'components/index';
import { createSlice } from 'utils/@reduxjs/toolkit';

import { ContainerState, VulnerabilityType } from './types';

// The initial state of the Tag container
export const initialState: ContainerState = {
  useVulnerabilityV3: false,
  resourceType: Resources.VirtualMachine,
  vulnerabilities: PagedQueryState.init([]),
  packageVulnerabilities: QueryState.init([]),
  riskContext: QueryState.init({}),
  patchablePackages: QueryState.init([]),
  cveHosts: PagedQueryState.init([]),
  cves: PagedQueryState.init([], {
    page: { info: { page_number: 1, page_size: 100 }, totalCount: 0 },
  }),
  openVulnerabilitiesBySeverity: QueryState.init({}),
  resolvedVulnerabilitiesBySeverity: QueryState.init({}),
  vulnerabilitiesBySeverityCounts: QueryState.init(
    {} as VulnerabilityBySeverityDashboard,
  ),

  hostsWithResolvedVulnerabilities: QueryState.init({}),
  hostsWithOpenVulnerabilities: QueryState.init({}),
  hostsWithResolvedVulnerabilitiesByType: QueryState.init({}),
  hostsWithOpenVulnerabilitiesByType: QueryState.init({}),
  containers: QueryState.init([]),
  containerVulnerability: QueryState.init({}),
  searchTerm: '',
  vulnerabilityType: VulnerabilityType.Open,
  sort: {
    sortOrder: 'desc',
    sortField: 'severity',
  },
  cveHostsRiskContext: QueryState.init({}),
  cveHostsVulnerabilities: {},
  publishedAt: publishedAtOptions[0],
  filters: {},
  cveDetails: QueryState.init({}),
  vulnerabilityPrioritization: QueryState.init(
    {} as VulnerabilityPrioritization,
  ),
  prioritizationFlow: QueryState.init({} as PrioritizationFlow),
  vulnerabilitiesBySources: QueryState.init(
    {} as VulnerabilityDashboardBySources,
  ),
  vulnerableImages: QueryState.init({} as SunburstDashboard),
  resourceInsight: QueryState.init({}),
  resourceDetails: QueryState.init({}),
  resourceAlertCount: QueryState.init({}),
  resourceVulnerabilities: PagedQueryState.init([]),
  resourceVulnerabilityCount: QueryState.init({}),
  resourceVulnerabilitySummary: QueryState.init({}),
  downloadReport: QueryState.init({}),
  ecrImages: PagedQueryState.init([]),
  ecrImageContainers: QueryState.init([]),
  containerRiskContext: QueryState.init({}),
  patchRecommendations: QueryState.init(
    {} as VulnerabilityPatchRecommendationResource,
  ),
};

const vulnerabilitySlice = createSlice({
  name: 'vulnerability',
  initialState,
  reducers: {
    setUseVulnerabilityV3(state, action: PayloadAction<boolean>) {
      state.useVulnerabilityV3 = action.payload;
    },
    getScanVaultDashboard(state, action: QueryAction<any, any>) {
      const mapped = action.payload.data
        ? mapDashboardToLegacy(action.payload.data)
        : undefined;
      const next = (current: any, data: any) =>
        QueryState.next(current, {
          ...action,
          payload: { ...action.payload, data },
        } as any) as any;

      state.vulnerabilitiesBySeverityCounts = next(
        state.vulnerabilitiesBySeverityCounts,
        mapped?.vulnerabilitiesBySeverityCounts,
      );
      state.vulnerabilitiesBySources = next(
        state.vulnerabilitiesBySources,
        mapped?.vulnerabilitiesBySources,
      );
      state.openVulnerabilitiesBySeverity = next(
        state.openVulnerabilitiesBySeverity,
        mapped?.openVulnerabilitiesBySeverity,
      );
      state.resolvedVulnerabilitiesBySeverity = next(
        state.resolvedVulnerabilitiesBySeverity,
        mapped?.resolvedVulnerabilitiesBySeverity,
      );
      state.hostsWithOpenVulnerabilities = next(
        state.hostsWithOpenVulnerabilities,
        mapped?.hostsWithOpenVulnerabilities,
      );
      state.hostsWithResolvedVulnerabilities = next(
        state.hostsWithResolvedVulnerabilities,
        mapped?.hostsWithResolvedVulnerabilities,
      );
      state.hostsWithOpenVulnerabilitiesByType = next(
        state.hostsWithOpenVulnerabilitiesByType,
        mapped?.hostsWithOpenVulnerabilitiesByType,
      );
      state.hostsWithResolvedVulnerabilitiesByType = next(
        state.hostsWithResolvedVulnerabilitiesByType,
        mapped?.hostsWithResolvedVulnerabilitiesByType,
      );
      state.vulnerabilityPrioritization = next(
        state.vulnerabilityPrioritization,
        mapped?.vulnerabilityPrioritization,
      );
      state.vulnerableImages = next(
        state.vulnerableImages,
        mapped?.vulnerableImages,
      );
      state.patchablePackages = next(
        state.patchablePackages,
        mapped?.patchablePackages,
      );
    },
    getPrioritizationFlow(
      state,
      action: QueryAction<PrioritizationFlow, PrioritizationFlowParams>,
    ) {
      state.prioritizationFlow = QueryState.next(
        state.prioritizationFlow,
        action,
      );
    },
    getScanVaultPrioritization(state, action: QueryAction<any, any>) {
      state.vulnerabilityPrioritization = QueryState.next(
        state.vulnerabilityPrioritization,
        action,
      );
    },
    getResourceInsight(
      state,
      action: QueryAction<InsightV2Pagination, InsightV2ApiInsightV2Request>,
    ) {
      state.resourceInsight = QueryState.next(state.resourceInsight, action, {
        mapData: r => r?.items?.[0],
      });
    },
    getResourceDetails(
      state,
      action: QueryAction<any, ResourceApiGetResourceByUuidRequest>,
    ) {
      state.resourceDetails = QueryState.next(state.resourceDetails, action);
    },
    getRiskContext(
      state,
      action: QueryAction<
        Record<string, RiskContextResponse[]>,
        ContextApiGetRiskContextResRequest
      >,
    ) {
      state.riskContext = QueryState.next(state.riskContext, action);
    },
    //get entity severity
    getResourceAlertCount(
      state,
      action: QueryAction<
        EntitySeverityListResponse,
        AlertsApiGetEntitySeverityListRequest
      >,
    ) {
      state.resourceAlertCount = QueryState.next(
        state.resourceAlertCount,
        action,
        {
          mapData: r => values(r?.severity)[0],
        },
      );
    },
    getResourceVulnerabilities(
      state,
      action: QueryAction<
        VulnerabilityPage,
        ItemApiGetVulnerabilityByIdRequest
      >,
    ) {
      state.resourceVulnerabilities = PagedQueryState.next(
        state.resourceVulnerabilities,
        action,
        {
          mapData: r => r.items ?? [],
          mapTotalCount: r => r.total ?? 0,
        },
      );
    },
    getAMIVulnerabilities(
      state,
      action: QueryAction<
        VulnerabilityPage,
        VulnerabilitiesApiGetVulnerabilityByIdRequest
      >,
    ) {
      state.resourceVulnerabilities = PagedQueryState.next(
        state.resourceVulnerabilities,
        action,
        {
          mapData: r => r.items ?? [],
          mapTotalCount: r => r.total ?? 0,
        },
      );
    },
    getResourceVulnerabilityCount(
      state,
      action: QueryAction<
        ResourceVulnerabilities[],
        VulnerabilitiesApiGetVulnerabilitiesRequest
      >,
    ) {
      state.resourceVulnerabilityCount = QueryState.next(
        state.resourceVulnerabilityCount,
        action,
        {
          mapData: r => r?.[0],
        },
      );
    },
    getResourceVulnerabilitySummary(
      state,
      action: QueryAction<
        InstanceSummary,
        ItemApiGetInstanceSummaryByIdRequest
      >,
    ) {
      state.resourceVulnerabilitySummary = QueryState.next(
        state.resourceVulnerabilitySummary,
        action,
      );
    },
    getECRImages(
      state,
      action: QueryAction<
        VulnerabilityImageDashboard,
        DefaultApiGetEcrImageListRequest
      >,
    ) {
      state.ecrImages = PagedQueryState.next(state.ecrImages, action, {
        mapData: r => r?.items,
        mapTotalCount: r => r?.total || 0,
      });
    },
    updateECRImagesPagination(state, action: PayloadAction<PageInfo>) {
      state.ecrImages.page.info = action.payload;
    },

    downloadReport(
      state,
      action: QueryAction<any, DefaultApiGetCsvReportRequest>,
    ) {
      state.downloadReport = QueryState.next(state.downloadReport, action);
    },

    getPatchRecommendations(
      state,
      action: QueryAction<
        VulnerabilityPatchRecommendationResource,
        ItemApiGetPatchRecommendationsForResourceRequest
      >,
    ) {
      state.patchRecommendations = QueryState.next(
        state.patchRecommendations,
        action,
      );
    },

    getECRImageContainers(
      state,
      action: QueryAction<ContainerInfo[], ItemApiGetContainersRequest>,
    ) {
      state.ecrImageContainers = QueryState.next(
        state.ecrImageContainers,
        action,
      );
    },
    getContainerRiskContext(
      state,
      action: QueryAction<
        Record<string, RiskContextResponse[]>,
        ContextApiGetRiskContextResRequest
      >,
    ) {
      state.containerRiskContext = QueryState.next(
        state.containerRiskContext,
        action,
      );
    },

    updateResourceVulnerabilitiesPagination(
      state,
      action: PayloadAction<PageInfo>,
    ) {
      state.resourceVulnerabilities.page.info = action.payload;
    },

    updateFilter(
      state,
      action: PayloadAction<{ field: string; value: string }>,
    ) {
      state.filters[action.payload.field] = action.payload.value;
    },

    resetFilters(state) {
      state.filters = initialState.filters;
    },
    getVulnerabilities(
      state,
      action: QueryAction<
        InsightV2Pagination,
        VulnerabilitiesApiVulnerabilityInsightRequest
      >,
    ) {
      state.vulnerabilities = PagedQueryState.next(
        state.vulnerabilities,
        action,
        {
          mapData: r => r?.items || [],
          mapTotalCount: r => r?.total || 0,
        },
      );
    },
    getEcrImages(
      state,
      action: QueryAction<
        VulnerabilityImageDashboard,
        DefaultApiGetEcrImageListRequest
      >,
    ) {
      state.vulnerabilities = PagedQueryState.next(
        state.vulnerabilities,
        action,
        {
          mapData: r => r?.items,
          mapTotalCount: r => r?.total || 0,
        },
      );
    },

    getCveVulnerabilities(
      state,
      action: QueryAction<VulnerabilityCvePage, DefaultApiGetCvesRequest>,
    ) {
      state.cves = PagedQueryState.next(state.cves, action, {
        mapData: r => r.items,
        mapTotalCount: r => r.total ?? 0,
      });
    },

    getPatchablePackages(
      state,
      action: QueryAction<
        VulnerablePackagePage,
        DefaultApiGetVulnerablePackagesRequest
      >,
    ) {
      state.patchablePackages = QueryState.next(
        state.patchablePackages,
        action,
        {
          mapData: r => r?.items ?? [],
        },
      );
    },
    getPackageVulnerabilities(
      state,
      action: QueryAction<
        VulnerablePackagePage,
        DefaultApiGetVulnerablePackagesRequest
      >,
    ) {
      state.vulnerabilities = PagedQueryState.next(
        state.vulnerabilities,
        action,
        {
          mapData: r => r?.items ?? [],
          mapTotalCount: r => r?.total ?? 0,
        },
      );
    },

    getCveHosts(
      state,
      action: QueryAction<
        InsightV2Pagination,
        VulnerabilitiesApiGetCveResourcesRequest
      >,
    ) {
      state.cveHosts = PagedQueryState.next(state.cveHosts, action, {
        mapData: r => r?.items,
        mapTotalCount: r => r?.total || 0,
      });
    },

    getPackageHosts(
      state,
      action: QueryAction<
        InsightV2Pagination,
        VulnerabilitiesApiGetPackageResourcesRequest
      >,
    ) {
      state.cveHosts = PagedQueryState.next(state.cveHosts, action, {
        mapData: r => r?.items,
        mapTotalCount: r => r?.total || 0,
      });
    },

    getOpenVulnerabilitiesBySeverity(
      state,
      action: QueryAction<
        any,
        DefaultApiGetVulnerabilitySeverityCountTrendRequest
      >,
    ) {
      state.openVulnerabilitiesBySeverity = QueryState.next(
        state.openVulnerabilitiesBySeverity,
        action,
        {
          mapData: res => {
            const severityData: Record<string, any> = {};
            forEach(res?.critical_vulnerabilities, (value, date) => {
              severityData[date] = {
                Critical: value,
                Medium: res?.medium_vulnerabilities?.[date],
                High: res?.high_vulnerabilities?.[date],
                Low: res?.low_vulnerabilities?.[date],
                Unknown: res?.unknown_vulnerabilities?.[date],
              };
            });
            return severityData;
          },
        },
      );
    },

    getResolvedVulnerabilitiesBySeverity(
      state,
      action: QueryAction<
        any,
        DefaultApiGetVulnerabilitySeverityCountTrendRequest
      >,
    ) {
      state.resolvedVulnerabilitiesBySeverity = QueryState.next(
        state.resolvedVulnerabilitiesBySeverity,
        action,
        {
          mapData: res => {
            const severityData: Record<string, any> = {};
            forEach(res?.critical_vulnerabilities, (value, date) => {
              severityData[date] = {
                Critical: value,
                Medium: res?.medium_vulnerabilities?.[date],
                High: res?.high_vulnerabilities?.[date],
                Low: res?.low_vulnerabilities?.[date],
                Unknown: res?.unknown_vulnerabilities?.[date],
              };
            });
            return severityData;
          },
        },
      );
    },

    getVulnerabilitiesBySeverityCounts(
      state,
      action: QueryAction<
        VulnerabilityBySeverityDashboard,
        DefaultApiGetBySeverityDashboardDataRequest
      >,
    ) {
      state.vulnerabilitiesBySeverityCounts = QueryState.next(
        state.vulnerabilitiesBySeverityCounts,
        action,
      );
    },

    getHostsWithOpenVulnerabilities(
      state,
      action: QueryAction<
        any,
        DefaultApiGetVulnerableResourcesCountTrendRequest
      >,
    ) {
      state.hostsWithOpenVulnerabilities = QueryState.next(
        state.hostsWithOpenVulnerabilities,
        action,
      );
    },
    getHostsWithResolvedVulnerabilities(
      state,
      action: QueryAction<
        any,
        DefaultApiGetVulnerableResourcesCountTrendRequest
      >,
    ) {
      state.hostsWithResolvedVulnerabilities = QueryState.next(
        state.hostsWithResolvedVulnerabilities,
        action,
      );
    },

    getHostsWithOpenVulnerabilitiesByType(
      state,
      action: QueryAction<
        any,
        DefaultApiGetVulnerableResourcesCountTrendRequest
      >,
    ) {
      state.hostsWithOpenVulnerabilitiesByType = QueryState.next(
        state.hostsWithOpenVulnerabilitiesByType,
        action,
      );
    },
    getHostsWithResolvedVulnerabilitiesByType(
      state,
      action: QueryAction<
        any,
        DefaultApiGetVulnerableResourcesCountTrendRequest
      >,
    ) {
      state.hostsWithResolvedVulnerabilitiesByType = QueryState.next(
        state.hostsWithResolvedVulnerabilitiesByType,
        action,
      );
    },

    getCveHostsRiskContext(
      state,
      action: QueryAction<
        Record<string, RiskContextResponse[]>,
        ContextApiGetRiskContextResRequest
      >,
    ) {
      state.cveHostsRiskContext = QueryState.next(
        state.cveHostsRiskContext,
        action,
      );
    },

    getCveHostsVulnerabilities(
      state,
      action: QueryAction<
        ResourceVulnerabilities[],
        VulnerabilitiesApiGetVulnerabilitiesRequest
      >,
    ) {
      const resourceType =
        action.payload.q.resourceVulnerabilitiesRequest.native_resource;
      if (!state.cveHostsVulnerabilities[resourceType!]) {
        state.cveHostsVulnerabilities[resourceType!] = QueryState.init(
          {} as any,
          {
            status: QueryStatus.pending,
          },
        );
        return;
      } else {
        state.cveHostsVulnerabilities[resourceType!] = QueryState.next(
          state.cveHostsVulnerabilities[resourceType!],
          action,
          {
            mapData: res => {
              let data: Record<string, any> = {};
              forEach(res, o => (data[o?.resource_id || ''] = o));
              return data;
            },
          },
        );
      }
    },

    getCVEDetails(
      state,
      action: QueryAction<VulnerabilityCvePage, DefaultApiGetCvesByIdRequest>,
    ) {
      state.cveDetails = QueryState.next(state.cveDetails, action, {
        mapData: r => r?.items?.[0],
      });
    },

    getVulnerabilityPrioritizationData(
      state,
      action: QueryAction<
        VulnerabilityPrioritization,
        DefaultApiGetVulnerabilityPriortizationDataRequest
      >,
    ) {
      state.vulnerabilityPrioritization = QueryState.next(
        state.vulnerabilityPrioritization,
        action,
      );
    },

    getVulnerabilitiesBySources(
      state,
      action: QueryAction<
        VulnerabilityDashboardBySources,
        DefaultApiGetBySourcesDashboardDataRequest
      >,
    ) {
      state.vulnerabilitiesBySources = QueryState.next(
        state.vulnerabilitiesBySources,
        action,
      );
    },

    getMostDeployedVulnerableImages(
      state,
      action: QueryAction<
        SunburstDashboard,
        DashboardApiGetResourcesByImagesForDashboardRequest
      >,
    ) {
      state.vulnerableImages = QueryState.next(state.vulnerableImages, action);
    },

    updateSearchTerm(state, action: PayloadAction<string>) {
      state.searchTerm = action.payload;
    },

    updateResourceType(state, action: PayloadAction<string>) {
      state.resourceType = action.payload;
    },

    updateVulnerabilityType(state, action: PayloadAction<string>) {
      state.vulnerabilityType = action.payload;
    },

    updateSortInfo(state, action: PayloadAction<Sorted>) {
      state.sort = action.payload;
    },

    updateVulnerabilityPageInfo(state, action: PayloadAction<PageInfo>) {
      state.vulnerabilities.page.info = action.payload;
    },

    updateCvePageInfo(state, action: PayloadAction<PageInfo>) {
      state.cves.page.info = action.payload;
    },

    updateCveHostsPageInfo(state, action: PayloadAction<PageInfo>) {
      state.cveHosts.page.info = action.payload;
    },

    updatePublishedAt(state, action: PayloadAction<Record<string, any>>) {
      state.publishedAt = action.payload;
    },
  },
});

export const { actions, reducer, name: sliceKey } = vulnerabilitySlice;
